import * as React from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';

import Box from '@components/layout/box';
import BoxHeader from '@components/layout/box-header';

import { PaymentState } from '../../../../store/payment/state';
import MyCardCtrl from '../../../payment/my-card';
import SeparatorLine from '@components/basics/separator-line';
import {
  AddPaymentMethod,
  GetPaymentMethods,
  RemovePaymentMethod,
} from '../../../../store/payment/action-creators';
import { Dispatch } from 'redux';
import Button from '@components/actions/button';
import StripeCardEditor from '../../../payment/stripe-card-editor';
import { ADD_PAYMENT_METHOD_ERROR } from '../../../../store/payment/actions';
import { StripePaymentData } from '../../../../types/stripe/payment-data';
import { useStripe } from '@stripe/react-stripe-js';
import { handlePaymentData } from '../../../../sdk/stripe/handle-payment-data';
import MotionOpacity from '@components/motion/motion-opacity';
import { AnimatePresence } from 'framer-motion';

interface CreditCardCtrlProps {
  className?: string;
  errorMsg: string;
  clientId?: string;
  cardList: PaymentState['methods'];
  isMobile: boolean;
  getPaymentMethods: (clientId: string) => GetPaymentMethods;
  addPaymentMethod: (
    clientId: string,
    paymentMethodId: string
  ) => AddPaymentMethod;
  removePaymentMethod: (
    clientId: string,
    paymentMethodId: string
  ) => RemovePaymentMethod;
}

const mapStateToProps = (state: AppState) => ({
  errorMsg: state.ui.errors[ADD_PAYMENT_METHOD_ERROR] || '',
  isMobile: state.ui.mobile,
  clientId: state.artist.active?.id,
  cardList: state.payment.methods,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getPaymentMethods: (clientId: string) =>
    dispatch(new GetPaymentMethods(clientId)),
  addPaymentMethod: (clientId: string, paymentMethodId: string) =>
    dispatch(new AddPaymentMethod(clientId, paymentMethodId)),
  removePaymentMethod: (clientId: string, paymentMethodId: string) =>
    dispatch(new RemovePaymentMethod(clientId, paymentMethodId)),
});

export const CreditCardCtrl: React.FC<CreditCardCtrlProps> = ({
  className,
  clientId,
  cardList,
  isMobile,
  errorMsg,
  addPaymentMethod,
  removePaymentMethod,
  getPaymentMethods,
}) => {
  const stripe = useStripe();
  const [showForm, setShowForm] = React.useState(false);

  React.useEffect(() => {
    if (clientId) {
      getPaymentMethods(clientId);
    }
  }, [getPaymentMethods, clientId]);

  const handleDelete = (paymentMethodId: string) => {
    if (clientId) {
      removePaymentMethod(clientId, paymentMethodId);
    }
  };

  const handleNewCard = async (paymentData: StripePaymentData) => {
    if (stripe) {
      const paymentMethod = await handlePaymentData(stripe, paymentData);

      if (clientId && paymentMethod?.id) {
        addPaymentMethod(clientId, paymentMethod.id);
        setShowForm(false);
      }
    }
  };

  if (!clientId || !cardList[clientId]?.length) {
    return null;
  }

  return (
    <Box className={className}>
      <BoxHeader title={<Translate id="ARTIST_PROFILE.BILLING.CARD" />} />
      {cardList[clientId].map((method) => (
        <MyCardCtrl
          key={method.id}
          className="mb-20"
          creditCard={method}
          active={false}
          disableCheckbox
          onDelete={
            cardList[clientId].length > 1
              ? () => handleDelete(method.id)
              : undefined
          }
        />
      ))}
      <AnimatePresence>
        {!showForm ? (
          <Button fullWidth onClick={() => setShowForm(!showForm)}>
            Add new credit card
          </Button>
        ) : (
          <MotionOpacity>
            <SeparatorLine />
            <StripeCardEditor
              onStart={() => null}
              onError={() => null}
              onSuccess={handleNewCard}
              errorMsg={errorMsg}
              isMobile={isMobile}
              customBtnLabel={'Add credit card'}
              customBtnLabelLoading={'Adding credit card...'}
            />
          </MotionOpacity>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CreditCardCtrl);
